import { createSystem, defaultConfig, defineConfig } from '@chakra-ui/react';
import { buttonRecipe } from './recipes/button.recipe';
import { inputRecipe } from './recipes/input.recipe';
import { textareaRecipe } from './recipes/textarea.recipe';

/**
 * Digitane AI Chakra UI v3 Theme
 * Custom theme preserving child-friendly design with 30+ colors, 17 gradients
 */

const customConfig = defineConfig({
  theme: {
    tokens: {
      colors: {
        primary: { value: '#5ba3f5' },
        primaryHover: { value: '#4a96e6' },
        primaryLight: { value: 'rgba(91, 163, 245, 0.15)' },
        secondary: { value: '#9b7fed' },
        secondaryHover: { value: '#8b6fdd' },
        gradientPrimary: {
          value: 'linear-gradient(135deg, #5ba3f5 0%, #9b7fed 100%)',
        },
        gradientSecondary: { value: 'linear-gradient(180deg, #dcd0f0 0%, #c8daf5 100%)' },

        // Semantic colors
        semantics: {
          success: { value: '#6bcf7a' },
          successLight: { value: 'rgba(107, 207, 122, 0.1)' },
          error: { value: '#ff6b7a' },
          errorLight: { value: '#fff5f7' },
          errorBorder: { value: '#ffd4db' },
          errorDark: { value: '#dc2626' },
          warning: { value: '#ffb547' },
          warningLight: { value: '#f59e0b' },
          warningDark: { value: '#ef4444' },
        },

        // Loading indicator colors
        loading: {
          box1: { value: '#b45ae6' },
          box2: { value: '#8c5ae6' },
          box3: { value: '#468ce6' },
          box4: { value: '#28c8e6' },
        },

        // Surface colors
        surfaces: {
          ai: { value: '#f8fafc' },
          user: { value: '#ffffff' },
          accent: { value: '#f6f5fd' },
        },

        // Text hierarchy
        text: {
          primary: { value: '#2d3748' },
          secondary: { value: '#718096' },
          inverse: { value: '#ffffff' },
          dark: { value: '#1f2937' },
          darkest: { value: '#111827' },
        },

        // Borders
        borders: {
          subtle: { value: '#e5e7eb' },
          default: { value: '#d1d5db' },
          emphasized: { value: '#9ca3af' },
          strong: { value: '#6b7280' },
          accent: { value: '#dfdcf5' },
        },
      },

      shadows: {
        xs: { value: '0 1px 2px rgba(0, 0, 0, 0.05)' },
        sm: { value: '0 1px 3px rgba(0, 0, 0, 0.1)' },
        md: { value: '0 2px 8px rgba(0, 0, 0, 0.1)' },
        lg: { value: '0 4px 6px rgba(0, 0, 0, 0.1)' },
        xl: { value: '0 4px 12px rgba(0, 0, 0, 0.15)' },
        '2xl': { value: '0 10px 40px rgba(0, 0, 0, 0.2)' },
        '3xl': { value: '0 20px 60px rgba(0, 0, 0, 0.3)' },
        inner: { value: 'inset 0 2px 4px rgba(0, 0, 0, 0.06)' },
        primaryGlow: { value: '0 4px 12px rgba(91, 163, 245, 0.3)' },
        errorGlow: { value: '0 2px 8px rgba(255, 107, 122, 0.3)' },
        focusRing: { value: '0 0 0 3px rgba(91, 163, 245, 0.15)' },
      },

      radii: {
        sm: { value: '6px' },
        md: { value: '8px' },
        lg: { value: '12px' },
        xl: { value: '16px' },
        '2xl': { value: '24px' },
        '3xl': { value: '32px' },
      },

      spacing: {
        '70': { value: '280px' },
      },

      sizes: {
        '70': { value: '280px' },
        chat: { value: '48rem' },
      },

      animations: {
        typingBounce: { value: 'typingBounce 1.4s infinite ease-in-out' },
        fadeIn: { value: 'fadeIn 0.3s ease-out' },
        fadeInScale: { value: 'fadeInScale 0.3s ease-out' },
        slideUp: { value: 'slideUp 0.3s ease-out' },
        slideIn: { value: 'slideIn 0.3s ease-out' },
        slideOut: { value: 'slideOut 0.3s ease-out' },
        pulse: { value: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite' },
        spin: { value: 'spin 0.8s linear infinite' },
        wave: { value: 'wave 2s linear infinite' },
        bounce: { value: 'bounce 1s infinite' },
        rotateBoxGray: { value: 'rotateBoxGray 2s ease-in-out infinite' },
        rotateBox1: { value: 'rotateBox1 2s ease-in-out infinite' },
        rotateBox2: { value: 'rotateBox2 2s ease-in-out infinite' },
        rotateBox3: { value: 'rotateBox3 2s ease-in-out infinite' },
        rotateBox4: { value: 'rotateBox4 2s ease-in-out infinite' },
      },
    },

    keyframes: {
      typingBounce: {
        '0%, 80%, 100%': {
          transform: 'scale(0.6)',
          opacity: '0.5',
        },
        '40%': {
          transform: 'scale(1)',
          opacity: '1',
        },
      },
      fadeIn: {
        '0%': {
          opacity: '0',
          transform: 'translateY(10px)',
        },
        '100%': {
          opacity: '1',
          transform: 'translateY(0)',
        },
      },
      fadeInScale: {
        '0%': {
          opacity: '0',
          transform: 'scale(0.9)',
        },
        '100%': {
          opacity: '1',
          transform: 'scale(1)',
        },
      },
      slideUp: {
        '0%': {
          opacity: '0',
          transform: 'translateY(20px)',
        },
        '100%': {
          opacity: '1',
          transform: 'translateY(0)',
        },
      },
      slideIn: {
        '0%': {
          opacity: '0',
          transform: 'translateX(100%)',
        },
        '100%': {
          opacity: '1',
          transform: 'translateX(0)',
        },
      },
      slideOut: {
        '0%': {
          opacity: '1',
          transform: 'translateX(0)',
        },
        '100%': {
          opacity: '0',
          transform: 'translateX(100%)',
        },
      },
      pulse: {
        '0%, 100%': {
          opacity: '1',
        },
        '50%': {
          opacity: '0.8',
        },
      },
      spin: {
        to: {
          transform: 'rotate(360deg)',
        },
      },
      wave: {
        '0%': {
          backgroundPosition: '200% 0',
        },
        '100%': {
          backgroundPosition: '-200% 0',
        },
      },
      bounce: {
        '0%, 100%': {
          transform: 'translateY(-25%)',
          animationTimingFunction: 'cubic-bezier(0.8, 0, 1, 1)',
        },
        '50%': {
          transform: 'translateY(0)',
          animationTimingFunction: 'cubic-bezier(0, 0, 0.2, 1)',
        },
      },
      rotateBoxGray: {
        '0%, 24%': {
          backgroundColor: '#8c8c8c',
        },
        '25%, 100%': {
          backgroundColor: '#d3d3d3',
        },
      },
      rotateBox1: {
        '0%, 24%': {
          backgroundColor: '#b45ae6',
        },
        '25%, 100%': {
          backgroundColor: '#8c8c8c',
        },
      },
      rotateBox2: {
        '0%, 24%': {
          backgroundColor: '#8c5ae6',
        },
        '25%, 100%': {
          backgroundColor: '#8c8c8c',
        },
      },
      rotateBox3: {
        '0%, 24%': {
          backgroundColor: '#468ce6',
        },
        '25%, 100%': {
          backgroundColor: '#8c8c8c',
        },
      },
      rotateBox4: {
        '0%, 24%': {
          backgroundColor: '#28c8e6',
        },
        '25%, 100%': {
          backgroundColor: '#8c8c8c',
        },
      },
    },

    // Custom recipes for component variants
    recipes: {
      button: buttonRecipe,
      input: inputRecipe,
      textarea: textareaRecipe,
    },
  },

  globalCss: {
    'html, body': {
      fontFamily:
        '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen, Ubuntu, Cantarell, sans-serif',
      bg: 'surfaces.ai',
      color: 'text.primary',
      minH: '100vh',
    },
    '#root, #widget-root': {
      minH: '100vh',
      display: 'flex',
      flexDirection: 'column',
    },
    // Screen reader only utility class
    '.sr-only': {
      position: 'absolute',
      width: '1px',
      height: '1px',
      padding: '0',
      margin: '-1px',
      overflow: 'hidden',
      clip: 'rect(0, 0, 0, 0)',
      whiteSpace: 'nowrap',
      borderWidth: '0',
    },
    '.drop-overlay': {
      pointerEvents: 'auto',
    },
    '.drop-error-message': {
      pointerEvents: 'none',
    },
    '.group': {
      position: 'relative',
    },
  },
});

export const system = createSystem(defaultConfig, customConfig);
export type System = typeof system;
